import { useEffect, useRef, useState } from 'react';
import {
  CalendarRange, Check, ChevronDown, ChevronLeft, ChevronRight, ChevronUp,
  Download, Pencil, Plus, Star, Trash2, Upload
} from 'lucide-react';
import {
  SPRINT_MAX_WEEKS,
  addDays,
  emptySprintDay,
  emptySprintScore,
  emptySprintWeek,
  isIsoDate,
  isSprintBoardEmpty,
  localDateKey,
  makeSprintId,
  mondayOf,
  moveListItem,
  readSprintBoardImport,
  sprintWeeks,
  updateSprintDay,
  updateSprintScore,
  updateSprintWeek
} from './lib/sprintBoard';

// The Calendar tab: a multi-week sprint, the daily routine that runs beside it, and a
// nightly scorecard. It owns no data. Every edit goes up through onChange as a
// board => board updater, and App turns the difference into per-date Firebase paths.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TONES = {
  '': { band: 'bg-zinc-100 text-zinc-700', bar: 'bg-zinc-400', row: 'border-l-zinc-200' },
  orange: { band: 'bg-orange-100 text-orange-900', bar: 'bg-orange-500', row: 'border-l-orange-400 bg-orange-50/70' },
  blue: { band: 'bg-sky-100 text-sky-900', bar: 'bg-sky-500', row: 'border-l-sky-400 bg-sky-50/70' },
  green: { band: 'bg-emerald-100 text-emerald-900', bar: 'bg-emerald-500', row: 'border-l-emerald-400 bg-emerald-50/70' }
};
const TONE_OPTIONS = [['', 'Plain'], ['orange', 'Orange'], ['blue', 'Blue'], ['green', 'Green']];

const INPUT = 'w-full min-w-0 rounded-lg border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 text-sm text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all';
const SELECT = 'rounded-lg border border-zinc-200 bg-zinc-50 py-1.5 px-2 text-xs text-zinc-700 focus:outline-none focus:ring-1 focus:ring-indigo-500/30 cursor-pointer';
const GHOST_BTN = 'inline-flex items-center gap-1.5 rounded-xl border border-zinc-200 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-600 hover:bg-zinc-50 transition-colors';
const SOLID_BTN = 'inline-flex items-center gap-1.5 rounded-xl bg-indigo-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-600 transition-colors';
const ICON_BTN = 'rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-zinc-400 transition-colors';
const ICON_BTN_DANGER = 'rounded-lg p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600 transition-colors';
const SECTION_LABEL = 'text-xs font-bold uppercase tracking-wider text-zinc-400';

const dateParts = (iso) => {
  const d = new Date(`${iso}T00:00:00Z`);
  const weekday = d.getUTCDay();
  return {
    day: d.getUTCDate(),
    month: MONTHS[d.getUTCMonth()],
    weekday: WEEKDAYS[weekday],
    weekend: weekday === 0 || weekday === 6
  };
};

const dateLabel = (iso) => {
  const p = dateParts(iso);
  return `${p.weekday} ${p.day} ${p.month}`;
};

const rangeLabel = (first, last) => {
  const a = dateParts(first);
  const b = dateParts(last);
  return a.month === b.month
    ? `${a.day}–${b.day} ${a.month}`
    : `${a.day} ${a.month} – ${b.day} ${b.month}`;
};

const clockToMinutes = (clock) => (clock ? Number(clock.slice(0, 2)) * 60 + Number(clock.slice(3, 5)) : null);

const patchById = (list, id, patch) => list.map((item) => (item.id === id ? { ...item, ...patch } : item));

// Grows a one-line textarea to fit its text, so a long task is read whole instead of
// scrolled sideways inside a single-line box.
const fitToContent = (el) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
};

// Text that commits on blur, not per keystroke: every commit is a Firebase write.
// `wrap` is one logical line that may wrap; `multiline` also accepts line breaks.
function DraftInput({ value, onCommit, multiline = false, wrap = false, className = '', ...rest }) {
  // null means "not being edited". The field then mirrors the saved value, so an
  // edit arriving from another device shows up without an effect to copy it across.
  const [draft, setDraft] = useState(null);
  const cancelled = useRef(false);
  const Field = multiline || wrap ? 'textarea' : 'input';
  return (
    <Field
      {...rest}
      // An inline ref is a new function each render, so React re-runs it after every
      // keystroke and every remote update — which is when the height needs redoing.
      {...(wrap ? { rows: 1, ref: (el) => fitToContent(el) } : {})}
      className={wrap ? `${className} resize-none overflow-hidden` : className}
      value={draft === null ? value : draft}
      onFocus={() => setDraft(value)}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (!cancelled.current && draft !== null && draft !== value) onCommit(draft);
        cancelled.current = false;
        setDraft(null);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          cancelled.current = true;
          e.currentTarget.blur();
        } else if (e.key === 'Enter' && !multiline) {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
    />
  );
}

// Adds on Enter or blur and stays put, so a run of tasks is typed without reaching
// for a button — and an empty row is never written.
function AddInput({ label, placeholder, onAdd }) {
  const [text, setText] = useState('');
  const submit = () => {
    const next = text.trim();
    if (next) onAdd(next);
    setText('');
  };
  return (
    <input
      className={INPUT}
      aria-label={label}
      placeholder={placeholder}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={submit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') submit();
        else if (e.key === 'Escape') setText('');
      }}
    />
  );
}

function RowControls({ label, index, count, onMove, onDelete }) {
  return (
    <div className="flex shrink-0 items-center">
      <button type="button" className={ICON_BTN} disabled={index === 0} onClick={() => onMove(-1)} aria-label={`Move ${label} up`}>
        <ChevronUp className="h-4 w-4" />
      </button>
      <button type="button" className={ICON_BTN} disabled={index === count - 1} onClick={() => onMove(1)} aria-label={`Move ${label} down`}>
        <ChevronDown className="h-4 w-4" />
      </button>
      <button type="button" className={ICON_BTN_DANGER} onClick={onDelete} aria-label={`Delete ${label}`}>
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

function ToneSelect({ label, value, onChange }) {
  return (
    <select aria-label={label} className={SELECT} value={value} onChange={(e) => onChange(e.target.value)}>
      {TONE_OPTIONS.map(([tone, name]) => <option key={tone} value={tone}>{name}</option>)}
    </select>
  );
}

export default function SprintBoardTab({ board, onChange, onImport, notify, syncLine }) {
  const [editing, setEditing] = useState(false);
  const [weekOpen, setWeekOpen] = useState({});
  const [pickedScoreDate, setPickedScoreDate] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const fileRef = useRef(null);

  // Drives the "today" ring and the NOW marker on the routine.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const clock = new Date(now);
  const today = localDateKey(clock);
  const nowMinutes = clock.getHours() * 60 + clock.getMinutes();

  const handleExport = () => {
    const blob = new Blob([JSON.stringify({ sprintBoard: board }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `calendar-${board.meta.startDate || 'empty'}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    notify('Calendar exported.');
  };

  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      let imported = null;
      try {
        imported = readSprintBoardImport(JSON.parse(String(reader.result)));
      } catch {
        imported = null;
      }
      if (!imported) {
        notify('That file is not a calendar export.');
        return;
      }
      if (!isSprintBoardEmpty(board)
        && !window.confirm('Replace the current calendar with this file? Ticks, scores and edits already here are overwritten.')) {
        return;
      }
      onImport(imported);
    };
    reader.readAsText(file);
  };

  const filePicker = (
    <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={handleImportFile} />
  );

  if (isSprintBoardEmpty(board)) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 py-10 text-center">
        <CalendarRange className="h-10 w-10 text-zinc-300" />
        <h2 className="text-lg font-bold text-zinc-800">No sprint set up yet</h2>
        <p className="max-w-sm text-sm text-zinc-500">
          Import a calendar file, or start a blank four-week sprint and fill it in here.
        </p>
        {syncLine}
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button type="button" className={SOLID_BTN} onClick={() => fileRef.current?.click()}>
            <Upload className="h-3.5 w-3.5" /> Import calendar
          </button>
          <button
            type="button"
            className={GHOST_BTN}
            onClick={() => {
              onChange((b) => ({ ...b, meta: { ...b.meta, startDate: mondayOf(today) } }));
              setEditing(true);
            }}
          >
            <Plus className="h-3.5 w-3.5" /> Start blank sprint
          </button>
        </div>
        {filePicker}
      </div>
    );
  }

  const { meta } = board;
  const weeks = sprintWeeks(board);
  const allDates = weeks.flatMap((week) => week.dates);
  const firstDate = allDates[0];
  const lastDate = allDates[allDates.length - 1];
  const sprintOver = today > lastDate;
  // A blank task is a row still being typed in edit mode; it is not work to count.
  const liveTasks = (date) => (board.days[date]?.tasks || []).filter((task) => task.text.trim());

  const sprintTasks = allDates.flatMap(liveTasks);
  const sprintDone = sprintTasks.filter((task) => task.done).length;
  const dayNumber = allDates.indexOf(today) + 1;
  const wins = allDates.filter((date) => board.scores[date]?.verdict === 'win').length;
  const losses = allDates.filter((date) => board.scores[date]?.verdict === 'loss').length;

  // --- EDITS ---

  const setMeta = (patch) => onChange((b) => ({ ...b, meta: { ...b.meta, ...patch } }));
  const editDay = (date, fn) => onChange((b) => updateSprintDay(b, date, fn));
  const editTasks = (date, fn) => editDay(date, (day) => ({ ...day, tasks: fn(day.tasks) }));
  const editWeek = (weekKey, patch) => onChange((b) => updateSprintWeek(b, weekKey, (week) => ({ ...week, ...patch })));
  const editRoutine = (fn) => onChange((b) => ({ ...b, routine: fn(b.routine) }));
  const editScoreFields = (fn) => onChange((b) => ({ ...b, scoreFields: fn(b.scoreFields) }));
  const editScore = (date, fn) => onChange((b) => updateSprintScore(b, date, fn));

  const deleteTask = (date, taskId) => {
    const before = board.days[date] || emptySprintDay();
    editTasks(date, (tasks) => tasks.filter((task) => task.id !== taskId));
    notify('Task deleted.', false, () => editDay(date, () => before));
  };

  const deleteRoutineRow = (rowId) => {
    const before = board.routine;
    editRoutine((rows) => rows.filter((row) => row.id !== rowId));
    notify('Block deleted.', false, () => editRoutine(() => before));
  };

  const deleteScoreField = (fieldId) => {
    const before = board.scoreFields;
    editScoreFields((fields) => fields.filter((field) => field.id !== fieldId));
    notify('Score line deleted.', false, () => editScoreFields(() => before));
  };

  // --- SPRINT ---

  const renderDay = (date, index) => {
    const p = dateParts(date);
    const day = board.days[date] || emptySprintDay();
    const tasks = liveTasks(date);
    const verdict = board.scores[date]?.verdict;
    const isToday = date === today;
    return (
      <div
        key={date}
        aria-current={isToday ? 'date' : undefined}
        className={`flex min-w-0 gap-3 rounded-xl border p-2 md:block ${day.mark ? 'border-orange-300 bg-orange-50/60' : 'border-zinc-200 bg-white'} ${isToday ? 'ring-2 ring-indigo-400' : ''}`}
      >
        <div className="flex w-12 shrink-0 flex-col gap-0.5 md:mb-1.5 md:w-auto md:flex-row md:items-baseline md:justify-between">
          <span className={`text-[10px] font-bold uppercase tracking-wide ${p.weekend ? 'text-sky-600' : 'text-zinc-400'}`}>
            {p.weekday}
          </span>
          <span className="flex items-center gap-1 text-xs font-semibold text-zinc-700">
            {p.day}{index === 0 || p.day === 1 ? ` ${p.month}` : ''}
            {verdict && (
              <span className={`rounded px-1 text-[9px] font-bold ${verdict === 'win' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                {verdict === 'win' ? 'W' : 'L'}
              </span>
            )}
          </span>
        </div>
        <div className="min-w-0 flex-1 space-y-1.5">
          {tasks.length ? tasks.map((task) => (
            <button
              key={task.id}
              type="button"
              role="checkbox"
              aria-checked={task.done}
              onClick={() => editTasks(date, (list) => patchById(list, task.id, { done: !task.done }))}
              className="flex w-full items-start gap-1.5 text-left"
            >
              <span
                className={`mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors md:h-4 md:w-4 ${
                  task.done
                    ? (task.key ? 'border-orange-500 bg-orange-500 text-white' : 'border-indigo-500 bg-indigo-500 text-white')
                    : (task.key ? 'border-orange-400 bg-orange-100' : 'border-zinc-300 bg-white')
                }`}
              >
                {task.done && <Check className="h-3 w-3" strokeWidth={3} />}
              </span>
              <span className={`min-w-0 text-sm leading-snug [overflow-wrap:anywhere] md:text-[11px] ${task.done ? 'text-zinc-400 line-through' : 'text-zinc-700'}`}>
                {task.text}
              </span>
            </button>
          )) : <span className="text-xs text-zinc-300">—</span>}
        </div>
      </div>
    );
  };

  const renderDayEditor = (date) => {
    const day = board.days[date] || emptySprintDay();
    const label = dateLabel(date);
    return (
      <div key={date} className={`min-w-0 space-y-2 rounded-xl border p-2 ${day.mark ? 'border-orange-300 bg-orange-50/60' : 'border-zinc-200'}`}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-bold text-zinc-700">{label}</span>
          <button
            type="button"
            aria-pressed={day.mark}
            onClick={() => editDay(date, (d) => ({ ...d, mark: !d.mark }))}
            className={`rounded-lg border px-2 py-1 text-[11px] font-semibold transition-colors ${day.mark ? 'border-orange-300 bg-orange-100 text-orange-800' : 'border-zinc-200 text-zinc-400 hover:bg-zinc-50'}`}
          >
            Highlight day
          </button>
        </div>
        {day.tasks.map((task, i) => (
          <div key={task.id} className="flex items-center gap-1">
            <button
              type="button"
              aria-pressed={task.key}
              aria-label={`Key task: ${task.text || 'untitled'}`}
              title="Key task"
              onClick={() => editTasks(date, (list) => patchById(list, task.id, { key: !task.key }))}
              className={ICON_BTN}
            >
              <Star className={`h-4 w-4 ${task.key ? 'fill-orange-400 text-orange-500' : ''}`} />
            </button>
            <DraftInput
              wrap
              className={INPUT}
              aria-label={`Task ${i + 1} on ${label}`}
              placeholder="Task"
              value={task.text}
              onCommit={(text) => editTasks(date, (list) => patchById(list, task.id, { text }))}
            />
            <RowControls
              label={`task ${i + 1} on ${label}`}
              index={i}
              count={day.tasks.length}
              onMove={(delta) => editTasks(date, (list) => moveListItem(list, i, delta))}
              onDelete={() => deleteTask(date, task.id)}
            />
          </div>
        ))}
        <AddInput
          label={`Add task on ${label}`}
          placeholder="Add task…"
          onAdd={(text) => editTasks(date, (list) => [...list, { id: makeSprintId('t'), text, done: false, key: false }])}
        />
      </div>
    );
  };

  const renderWeek = (week, weekIndex) => {
    const info = board.weeks[week.key] || emptySprintWeek();
    const tone = TONES[info.tone] || TONES[''];
    const tasks = week.dates.flatMap(liveTasks);
    const done = tasks.filter((task) => task.done).length;
    // Weeks already behind you fold away, unless the whole sprint is finished and
    // being read back.
    const open = weekOpen[week.key] ?? (week.dates[6] >= today || sprintOver);
    return (
      <div key={week.key} className="overflow-hidden rounded-2xl border border-zinc-200">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setWeekOpen((prev) => ({ ...prev, [week.key]: !open }))}
          className={`flex w-full items-center gap-2 px-3 py-2 text-left ${tone.band}`}
        >
          {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
          <span className="min-w-0 flex-1 text-xs font-bold uppercase tracking-wide [overflow-wrap:anywhere]">
            Week {weekIndex + 1}
            <span className="font-medium normal-case opacity-70"> ({rangeLabel(week.dates[0], week.dates[6])})</span>
            {info.title ? ` — ${info.title}` : ''}
          </span>
          <span className="shrink-0 text-[11px] font-semibold tabular-nums opacity-80">{done}/{tasks.length}</span>
        </button>
        <div className="h-1 bg-zinc-100">
          <div className={`h-full transition-all ${tone.bar}`} style={{ width: `${tasks.length ? (done / tasks.length) * 100 : 0}%` }} />
        </div>
        {open && (
          <div className="p-2 sm:p-3">
            {editing && (
              <div className="mb-3 flex items-center gap-2">
                <DraftInput
                  className={INPUT}
                  aria-label={`Week ${weekIndex + 1} title`}
                  placeholder="Week theme"
                  value={info.title}
                  onCommit={(title) => editWeek(week.key, { title })}
                />
                <ToneSelect label={`Week ${weekIndex + 1} colour`} value={info.tone} onChange={(value) => editWeek(week.key, { tone: value })} />
              </div>
            )}
            <div className={editing ? 'grid grid-cols-1 gap-2 sm:grid-cols-2' : 'grid grid-cols-1 gap-1.5 md:grid-cols-7'}>
              {week.dates.map(editing ? renderDayEditor : renderDay)}
            </div>
          </div>
        )}
      </div>
    );
  };

  // --- ROUTINE ---

  const nowIndex = board.routine.findIndex((row, i) => {
    const start = clockToMinutes(row.start);
    if (start === null) return false;
    // A block with no end time runs until the next block that has a start.
    const next = board.routine.slice(i + 1).find((later) => later.start);
    const end = clockToMinutes(row.end) ?? clockToMinutes(next?.start) ?? start + 30;
    return nowMinutes >= start && nowMinutes < end;
  });

  const renderRoutineRow = (row, i) => {
    const tone = TONES[row.tone] || TONES[''];
    if (editing) {
      return (
        <li key={row.id} className={`space-y-2 rounded-xl border border-l-4 border-zinc-200 p-2 ${tone.row}`}>
          <div className="flex flex-wrap items-center gap-2">
            <DraftInput
              type="time"
              className={`${INPUT} w-auto`}
              aria-label={`Start time, block ${i + 1}`}
              value={row.start}
              onCommit={(start) => editRoutine((rows) => patchById(rows, row.id, { start }))}
            />
            <span className="text-zinc-400">–</span>
            <DraftInput
              type="time"
              className={`${INPUT} w-auto`}
              aria-label={`End time, block ${i + 1}`}
              value={row.end}
              onCommit={(end) => editRoutine((rows) => patchById(rows, row.id, { end }))}
            />
            <ToneSelect label={`Colour, block ${i + 1}`} value={row.tone} onChange={(value) => editRoutine((rows) => patchById(rows, row.id, { tone: value }))} />
            <div className="ml-auto">
              <RowControls
                label={`block ${i + 1}`}
                index={i}
                count={board.routine.length}
                onMove={(delta) => editRoutine((rows) => moveListItem(rows, i, delta))}
                onDelete={() => deleteRoutineRow(row.id)}
              />
            </div>
          </div>
          <DraftInput
            className={`${INPUT} font-semibold`}
            aria-label={`Name, block ${i + 1}`}
            placeholder="Block name"
            value={row.title}
            onCommit={(title) => editRoutine((rows) => patchById(rows, row.id, { title }))}
          />
          <DraftInput
            wrap
            className={INPUT}
            aria-label={`Details, block ${i + 1}`}
            placeholder="Details"
            value={row.note}
            onCommit={(note) => editRoutine((rows) => patchById(rows, row.id, { note }))}
          />
        </li>
      );
    }
    const isNow = i === nowIndex;
    return (
      <li key={row.id} className={`flex gap-3 rounded-lg border-l-4 px-3 py-2 ${tone.row} ${isNow ? 'ring-1 ring-indigo-300' : ''}`}>
        <div className="w-[5.5rem] shrink-0 pt-px text-xs font-semibold tabular-nums text-zinc-500">
          {row.start}{row.end ? `–${row.end}` : ''}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wide text-zinc-800 [overflow-wrap:anywhere]">{row.title}</span>
            {isNow && <span className="rounded-full bg-indigo-500 px-1.5 py-0.5 text-[9px] font-bold leading-none text-white">NOW</span>}
          </div>
          {row.note && <p className="text-xs leading-snug text-zinc-500 [overflow-wrap:anywhere]">{row.note}</p>}
        </div>
      </li>
    );
  };

  // --- SCORECARD ---

  const scoreDate = pickedScoreDate && pickedScoreDate >= firstDate && pickedScoreDate <= lastDate
    ? pickedScoreDate
    : (today < firstDate ? firstDate : (sprintOver ? lastDate : today));
  const score = board.scores[scoreDate] || emptySprintScore();

  const renderScoreField = (field, i) => {
    const value = score.values[field.id] || '';
    const setValue = (next) => editScore(scoreDate, (s) => ({ ...s, values: { ...s.values, [field.id]: next } }));
    return (
      <div key={field.id} className="min-w-0 rounded-xl border border-zinc-200 p-2">
        <div className="mb-1.5 text-[11px] font-semibold leading-tight text-zinc-500 [overflow-wrap:anywhere]">
          <span className="tabular-nums text-zinc-400">{i + 1}.</span> {field.label}
        </div>
        {field.type === 'yesno' ? (
          <div className="flex gap-1.5">
            {[['y', 'Yes'], ['n', 'No']].map(([code, name]) => (
              <button
                key={code}
                type="button"
                aria-pressed={value === code}
                onClick={() => setValue(value === code ? '' : code)}
                className={`flex-1 rounded-lg border py-1.5 text-xs font-semibold transition-colors ${value === code ? 'border-indigo-500 bg-indigo-500 text-white' : 'border-zinc-200 text-zinc-500 hover:bg-zinc-50'}`}
              >
                {name}
              </button>
            ))}
          </div>
        ) : (
          <DraftInput
            className={`${INPUT} tabular-nums`}
            inputMode="decimal"
            aria-label={`${field.label}, ${dateLabel(scoreDate)}`}
            placeholder="0"
            value={value}
            onCommit={setValue}
          />
        )}
      </div>
    );
  };

  const renderScoreFieldEditor = (field, i) => (
    <li key={field.id} className="flex items-center gap-2">
      <span className="w-5 shrink-0 text-right text-xs tabular-nums text-zinc-400">{i + 1}.</span>
      <DraftInput
        className={INPUT}
        aria-label={`Score line ${i + 1}`}
        placeholder="What you count"
        value={field.label}
        onCommit={(label) => editScoreFields((fields) => patchById(fields, field.id, { label }))}
      />
      <select
        aria-label={`Score line ${i + 1} type`}
        className={SELECT}
        value={field.type}
        onChange={(e) => editScoreFields((fields) => patchById(fields, field.id, { type: e.target.value }))}
      >
        <option value="number">Number</option>
        <option value="yesno">Yes / No</option>
      </select>
      <RowControls
        label={`score line ${i + 1}`}
        index={i}
        count={board.scoreFields.length}
        onMove={(delta) => editScoreFields((fields) => moveListItem(fields, i, delta))}
        onDelete={() => deleteScoreField(field.id)}
      />
    </li>
  );

  return (
    <div className="space-y-6">
      <div className="text-center">
        {editing ? (
          <div className="space-y-2 text-left">
            <DraftInput
              className={`${INPUT} font-bold`}
              aria-label="Calendar title"
              placeholder="Title"
              value={meta.title}
              onCommit={(title) => setMeta({ title })}
            />
            <DraftInput
              className={INPUT}
              aria-label="Calendar subtitle"
              placeholder="Rules line under the title"
              value={meta.subtitle}
              onCommit={(subtitle) => setMeta({ subtitle })}
            />
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-zinc-500">
              <label className="flex items-center gap-2">
                Starts
                <DraftInput
                  type="date"
                  className={`${INPUT} w-auto`}
                  value={meta.startDate}
                  // A cleared date would read as "no sprint" and hide everything.
                  onCommit={(startDate) => { if (isIsoDate(startDate)) setMeta({ startDate }); }}
                />
              </label>
              <label className="flex items-center gap-2">
                Weeks
                <select className={SELECT} value={meta.weekCount} onChange={(e) => setMeta({ weekCount: Number(e.target.value) })}>
                  {Array.from({ length: SPRINT_MAX_WEEKS }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
                </select>
              </label>
              <span>Tasks stay on their own dates when the start moves.</span>
            </div>
          </div>
        ) : (
          <>
            <h2 className="text-lg font-bold text-zinc-800 [overflow-wrap:anywhere]">{meta.title || 'Calendar'}</h2>
            {meta.subtitle && <p className="text-sm text-zinc-500 [overflow-wrap:anywhere]">{meta.subtitle}</p>}
          </>
        )}
        {syncLine}
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          <button type="button" aria-pressed={editing} className={editing ? SOLID_BTN : GHOST_BTN} onClick={() => setEditing((v) => !v)}>
            {editing ? <Check className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
            {editing ? 'Done editing' : 'Edit'}
          </button>
          <button type="button" className={GHOST_BTN} onClick={handleExport}>
            <Download className="h-3.5 w-3.5" /> Export
          </button>
          <button type="button" className={GHOST_BTN} onClick={() => fileRef.current?.click()}>
            <Upload className="h-3.5 w-3.5" /> Import
          </button>
          {filePicker}
        </div>
      </div>

      <section>
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <h3 className={SECTION_LABEL}>{weeks.length}-week sprint · {rangeLabel(firstDate, lastDate)}</h3>
          <span className="text-xs tabular-nums text-zinc-500">
            {dayNumber > 0 ? `Day ${dayNumber} of ${allDates.length} · ` : ''}{sprintDone}/{sprintTasks.length} tasks done
          </span>
        </div>
        <div className="space-y-3">{weeks.map(renderWeek)}</div>
      </section>

      <section>
        <h3 className={`mb-2 ${SECTION_LABEL}`}>Daily operating schedule</h3>
        {board.routine.length > 0 && (
          <ol className={editing ? 'space-y-2' : 'space-y-1'}>{board.routine.map(renderRoutineRow)}</ol>
        )}
        {!board.routine.length && !editing && (
          <p className="text-sm text-zinc-400">No daily blocks yet — press Edit to add them.</p>
        )}
        {editing && (
          <button
            type="button"
            className={`mt-2 ${GHOST_BTN}`}
            onClick={() => editRoutine((rows) => [...rows, { id: makeSprintId('r'), start: '', end: '', title: '', note: '', tone: '' }])}
          >
            <Plus className="h-3.5 w-3.5" /> Add block
          </button>
        )}
      </section>

      <section>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 className={SECTION_LABEL}>Nightly scorecard</h3>
          {!editing && (
            <div className="flex items-center gap-1">
              <button
                type="button"
                className={ICON_BTN}
                aria-label="Previous day"
                disabled={scoreDate <= firstDate}
                onClick={() => setPickedScoreDate(addDays(scoreDate, -1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="min-w-[5.5rem] text-center text-xs font-semibold text-zinc-700">{dateLabel(scoreDate)}</span>
              <button
                type="button"
                className={ICON_BTN}
                aria-label="Next day"
                disabled={scoreDate >= lastDate}
                onClick={() => setPickedScoreDate(addDays(scoreDate, 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              {scoreDate !== today && today >= firstDate && !sprintOver && (
                <button type="button" className={GHOST_BTN} onClick={() => setPickedScoreDate(null)}>Today</button>
              )}
            </div>
          )}
        </div>
        {editing ? (
          <>
            <ol className="space-y-2">{board.scoreFields.map(renderScoreFieldEditor)}</ol>
            <button
              type="button"
              className={`mt-2 ${GHOST_BTN}`}
              onClick={() => editScoreFields((fields) => [...fields, { id: makeSprintId('s'), label: '', type: 'number' }])}
            >
              <Plus className="h-3.5 w-3.5" /> Add score line
            </button>
          </>
        ) : (
          <>
            {board.scoreFields.length > 0 && (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">{board.scoreFields.map(renderScoreField)}</div>
            )}
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-1.5">
                <span className="mr-1 text-xs font-semibold text-zinc-500">Verdict</span>
                {[['win', 'Win', 'border-emerald-500 bg-emerald-500 text-white'], ['loss', 'Loss', 'border-rose-500 bg-rose-500 text-white']].map(([code, name, active]) => (
                  <button
                    key={code}
                    type="button"
                    aria-pressed={score.verdict === code}
                    onClick={() => editScore(scoreDate, (s) => ({ ...s, verdict: s.verdict === code ? '' : code }))}
                    className={`rounded-lg border px-4 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${score.verdict === code ? active : 'border-zinc-200 text-zinc-500 hover:bg-zinc-50'}`}
                  >
                    {name}
                  </button>
                ))}
              </div>
              <span className="text-xs tabular-nums text-zinc-500">Sprint so far: {wins}W · {losses}L</span>
            </div>
          </>
        )}
      </section>

      {editing ? (
        <DraftInput
          multiline
          rows={3}
          className={INPUT}
          aria-label="Footnote"
          placeholder="Footnote — rules, definitions, reminders"
          value={meta.footnote}
          onCommit={(footnote) => setMeta({ footnote })}
        />
      ) : meta.footnote && (
        <p className="whitespace-pre-wrap border-t border-zinc-100 pt-3 text-xs leading-relaxed text-zinc-500 [overflow-wrap:anywhere]">
          {meta.footnote}
        </p>
      )}
    </div>
  );
}
